
import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { TwitterSearchResult } from '@/types/twitterTypes';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useTheme } from '@/contexts/ThemeContext';
import { Pagination } from '@/components/ui/pagination';
import { RefreshCw, Plus, Heart, MessageCircle, Repeat, BarChart3, ExternalLink, ChevronDown, ChevronUp, User } from 'lucide-react';
import { format, formatDistanceToNow } from 'date-fns';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { useToast } from '@/hooks/use-toast';
import { Card, CardContent } from '@/components/ui/card';
import TweetKolTrackerDialog from './TweetKolTrackerDialog';
import { useTwitterJobStatus } from '@/hooks/use-twitter-job-status';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const PAGE_SIZE_OPTIONS = [9, 18, 30, 60];

interface TweetsByKol {
  [key: string]: TwitterSearchResult[];
}

const DeliverableTweetsView = ({ deliverableId }: { deliverableId: string }) => {
  const [tweets, setTweets] = useState<TwitterSearchResult[]>([]);
  const [tweetsByKol, setTweetsByKol] = useState<TweetsByKol>({});
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const [totalTweets, setTotalTweets] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTweet, setSelectedTweet] = useState<TwitterSearchResult | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deliverableName, setDeliverableName] = useState<string>("");
  const { theme } = useTheme();
  const { toast } = useToast();
  const isLightMode = theme === 'light';
  const { 
    jobStatus, 
    processingProgress, 
    errorMessage, 
    deliverableDetails,
    startBackgroundProcessing,
    fetchDeliverableDetails 
  } = useTwitterJobStatus(deliverableId);

  // Fetch deliverable name when component mounts
  useEffect(() => {
    const fetchDeliverableName = async () => {
      if (!deliverableId) return;
      
      try {
        const { data, error } = await supabase
          .from('twitter_deliverables')
          .select('name')
          .eq('id', deliverableId)
          .single();
          
        if (error) {
          console.error('Error fetching deliverable name:', error);
          return;
        }
        
        if (data) {
          setDeliverableName(data.name);
        }
      } catch (err) {
        console.error('Exception fetching deliverable name:', err);
      }
    };
    
    fetchDeliverableName();
  }, [deliverableId]);

  const fetchTweets = useCallback(async () => {
    if (!deliverableId) return;

    setIsLoading(true);
    try {
      console.log(`Fetching tweets for deliverable: ${deliverableId}`);
      
      let query = supabase
        .from('twitter_search_results')
        .select('*', { count: 'exact' })
        .eq('deliverable_id', deliverableId)
        .order('created_at', { ascending: false });

      // Search in text or author fields
      if (searchTerm) {
        query = query.or(`text.ilike.%${searchTerm}%,author_username.ilike.%${searchTerm}%,author_name.ilike.%${searchTerm}%`);
      }

      const { data, error, count } = await query
        .range((page - 1) * pageSize, page * pageSize - 1);

      if (error) {
        console.error('Error fetching tweets:', error);
        toast({
          title: 'Error',
          description: 'Failed to fetch tweets',
          variant: 'destructive',
        });
      } else {
        console.log(`Found ${data?.length || 0} tweets for deliverable ${deliverableId}`);
        
        // Add deliverable name to each tweet
        const enhancedData = data?.map(tweet => ({
          ...tweet,
          deliverable_name: deliverableName
        })) || [];
        
        setTweets(enhancedData as TwitterSearchResult[]);
        setTotalTweets(count || 0);

        // Group tweets by KOL
        const groupedTweets: TweetsByKol = {};
        enhancedData.forEach(tweet => {
          const kolKey = tweet.author_username || 'unknown';
          if (!groupedTweets[kolKey]) {
            groupedTweets[kolKey] = [];
          }
          groupedTweets[kolKey].push(tweet as TwitterSearchResult);
        });
        setTweetsByKol(groupedTweets);
      }
    } catch (err) {
      console.error('Exception fetching tweets:', err);
    } finally {
      setIsLoading(false);
    }
  }, [deliverableId, page, pageSize, searchTerm, toast, deliverableName]);

  // Fetch tweets initially and when dependencies change
  useEffect(() => {
    fetchTweets();
  }, [fetchTweets]);

  // Auto-refresh when job status changes to completed
  useEffect(() => {
    if (jobStatus === 'completed') {
      console.log('Job completed, refreshing tweets...');
      fetchTweets();
    }
  }, [jobStatus, fetchTweets]);
  
  // Check if we need to trigger processing if there are no tweets but job is completed
  useEffect(() => {
    const checkAndProcessIfNeeded = async () => {
      if (jobStatus === 'completed' && totalTweets === 0 && !isLoading) {
        console.log('Job is completed but no tweets found, checking details...');
        await fetchDeliverableDetails();
        
        if (deliverableDetails) {
          const processingDetails = deliverableDetails.processing_details ? 
            JSON.parse(deliverableDetails.processing_details) : null;
            
          // If processing found 0 tweets, we might need to retry
          if (processingDetails && processingDetails.tweets_found === 0) {
            console.log('Previous processing found 0 tweets, consider retrying with background processing');
          }
        }
      }
    };
    
    checkAndProcessIfNeeded();
  }, [jobStatus, totalTweets, isLoading, deliverableDetails, fetchDeliverableDetails]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1); // Reset to first page
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
    setPage(1); // Reset to first page
  };

  const handleRefresh = async () => {
    fetchTweets();
  };
  
  const handleRetryProcessing = async () => {
    console.log('Retrying background processing...');
    if (deliverableDetails) {
      try {
        toast({
          title: "Processing Started",
          description: "Starting tweet collection process..."
        });
        
        // Start background processing
        const success = await startBackgroundProcessing();
        
        if (success) {
          toast({
            title: "Processing Initiated",
            description: "Tweet collection has been started. This may take a few minutes."
          });
        }
      } catch (error) {
        console.error('Error starting background processing:', error);
        toast({
          title: "Error",
          description: "Failed to start tweet collection process",
          variant: "destructive"
        });
      }
    }
  };

  const handleOpenKolTrackerDialog = (tweet: TwitterSearchResult) => {
    setSelectedTweet(tweet);
    setIsDialogOpen(true);
  };

  const renderPagination = () => {
    const totalPages = Math.ceil(totalTweets / pageSize);

    if (totalPages <= 1) {
      return null;
    }

    return (
      <div className="flex items-center justify-between mt-4">
        <div className="text-sm text-muted-foreground">
          Showing <span className="font-medium">{(page - 1) * pageSize + 1}</span> to{' '}
          <span className="font-medium">{Math.min(page * pageSize, totalTweets)}</span> of{' '}
          <span className="font-medium">{totalTweets}</span> tweets
        </div>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </div>
    );
  };

  const renderTweet = (tweet: TwitterSearchResult) => {
    const tweetDate = tweet.created_at ? new Date(tweet.created_at) : new Date();
    const timeAgo = formatDistanceToNow(tweetDate, { addSuffix: true });
    const formattedDate = format(tweetDate, 'MMM d, yyyy');

    return (
      <Card 
        key={tweet.id} 
        className="h-full flex flex-col hover:shadow-md transition-all duration-300 transform hover:-translate-y-0.5"
      >
        <CardContent className="p-3 flex flex-col h-full">
          <div className="flex items-start space-x-2">
            <Avatar className="h-8 w-8 rounded-full flex-shrink-0">
              <AvatarImage 
                src={tweet.profile_image_url || 'https://abs.twimg.com/sticky/default_profile_images/default_profile.png'} 
                alt={tweet.author_name || tweet.author_username || 'Unknown'}
              />
              <AvatarFallback>{(tweet.author_name || 'U')[0].toUpperCase()}</AvatarFallback>
            </Avatar>

            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-1">
                <div className="truncate">
                  <span className="font-medium text-xs">
                    {tweet.author_name || 'Unknown'}
                  </span>
                  <div className="text-muted-foreground text-xs truncate">
                    @{tweet.author_username || 'unknown'} • {timeAgo}
                  </div>
                </div>
                <div className="flex items-center ml-1 flex-shrink-0">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleOpenKolTrackerDialog(tweet)}
                    className="h-6 w-6 p-0 rounded-full"
                    title="Add to KOL Tracker"
                  >
                    <Plus className="h-3 w-3" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => window.open(`https://twitter.com/i/web/status/${tweet.tweet_id}`, '_blank')}
                    className="h-6 w-6 p-0 rounded-full"
                    title="View on Twitter"
                  >
                    <ExternalLink className="h-3 w-3" />
                  </Button>
                </div>
              </div>

              <p className="text-xs mt-1 line-clamp-3 break-words">{tweet.text}</p>
            </div>
          </div>

          <div className="flex items-center justify-between mt-auto pt-2 text-xs text-muted-foreground">
            <div className="flex items-center space-x-3">
              <span className="flex items-center gap-1">
                <MessageCircle className="h-3 w-3" /> {tweet.reply_count || 0}
              </span>
              <span className="flex items-center gap-1">
                <Repeat className="h-3 w-3" /> {tweet.retweet_count || 0}
              </span>
              <span className="flex items-center gap-1">
                <Heart className="h-3 w-3" /> {tweet.like_count || 0}
              </span>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              {tweet.deliverable_name && (
                <Badge variant="primary" className="text-[10px] py-0 px-1">
                  {tweet.deliverable_name}
                </Badge>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderKolSection = (kolUsername: string, kolTweets: TwitterSearchResult[]) => {
    const firstTweet = kolTweets[0];
    
    return (
      <AccordionItem key={kolUsername} value={kolUsername}>
        <AccordionTrigger className="py-2 px-3 bg-muted/50 rounded-md hover:no-underline">
          <div className="flex items-center gap-2">
            <Avatar className="h-6 w-6">
              <AvatarImage 
                src={firstTweet.profile_image_url || ''}
                alt={firstTweet.author_name || firstTweet.author_username || ''}
              />
              <AvatarFallback><User className="h-3 w-3" /></AvatarFallback>
            </Avatar>
            <div>
              <span className="font-medium text-sm">
                {firstTweet.author_name || kolUsername}
              </span>
              <span className="text-xs text-muted-foreground ml-2">
                @{kolUsername} • {kolTweets.length} tweets
              </span>
            </div>
          </div>
        </AccordionTrigger>
        
        <AccordionContent className="pt-3">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {kolTweets.map(tweet => (
              <div key={tweet.id}>
                {renderTweet(tweet)}
              </div>
            ))}
          </div>
        </AccordionContent>
      </AccordionItem>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="relative w-full md:w-auto md:flex-1 md:max-w-md">
          <Input
            type="text"
            placeholder="Search tweets or KOL usernames..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pr-8"
          />
        </div>
        
        <div className="flex items-center space-x-2">
          <Select value={String(pageSize)} onValueChange={(value) => handlePageSizeChange(Number(value))}>
            <SelectTrigger className="w-[130px]">
              <SelectValue placeholder="Tweets per page" />
            </SelectTrigger>
            <SelectContent>
              {PAGE_SIZE_OPTIONS.map((size) => (
                <SelectItem key={size} value={String(size)}>{size} per page</SelectItem>
              ))}
            </SelectContent>
          </Select>
          
          <Button
            variant="outline"
            onClick={handleRefresh}
            disabled={isLoading}
            className="flex items-center gap-2"
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            <span className="hidden sm:inline">Refresh</span>
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="flex flex-col items-center">
            <RefreshCw className="h-8 w-8 animate-spin text-primary mb-2" />
            <p className="text-muted-foreground">Loading tweets...</p>
          </div>
        </div>
      ) : tweets.length === 0 ? (
        <div className="text-center py-12 border rounded-lg bg-muted/10">
          <p className="text-lg font-medium mb-2">No tweets found</p>
          <p className="text-muted-foreground mb-6">
            {searchTerm 
              ? "Try adjusting your search terms" 
              : jobStatus === 'completed' 
                ? "No tweets were found for this deliverable's criteria." 
                : "Processing hasn't started or is in progress."
            }
          </p>
          
          {jobStatus === 'completed' && (
            <div className="flex flex-col items-center gap-2">
              <p className="text-sm text-muted-foreground">
                Consider adjusting the query parameters or trying again
              </p>
              <Button 
                onClick={handleRetryProcessing}
                className="mt-2"
              >
                Retry Processing
              </Button>
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* Render tweets grouped by KOL using Accordion */}
          <Accordion type="multiple" className="space-y-2" defaultValue={Object.keys(tweetsByKol)}>
            {Object.entries(tweetsByKol).map(([kolUsername, kolTweets]) => 
              renderKolSection(kolUsername, kolTweets)
            )}
          </Accordion>
          {renderPagination()}
        </div>
      )}

      {selectedTweet && (
        <TweetKolTrackerDialog
          tweet={selectedTweet}
          isOpen={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
        />
      )}
    </div>
  );
};

export default DeliverableTweetsView;
